import CloseIcon from '@mui/icons-material/Close';
import * as turf from "@turf/turf";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useMap } from "react-map-gl";
import { useDrawMapStore } from "../..";
import { useConditionallyClosable } from "../../hooks/useClosable";
import useIsHalfScreenMap from "../../hooks/useIsHalfScreenMap";
import { ClosableOrder } from "../../store/ClosableStore";
import { DrawMapMode } from "../../store/DrawMapStore";
import { formatNumber } from "../../utils/utils";
import commonStyles from '../Common.module.css';
import Dropdown from "../Dropdown";
import MeasureIcon from "../icons/MeasureIcon";
import styles from './Header.module.css';

function MeasureDropdown() {
    const { map } = useMap();
    const isHalfScreenMap = useIsHalfScreenMap();
    const drawMapStore = useDrawMapStore();
    const [areMeasureToolsOpen, setAreMeasureToolsOpen] = useState(false);
    useConditionallyClosable(ClosableOrder.Medium, () => drawMapStore.clear(), [DrawMapMode.MeasureArea, DrawMapMode.MeasureDistance].includes(drawMapStore.mode));

    const getElevationChangeFt = () => {
        const coords = (drawMapStore.result!.features[0]?.geometry as any)?.coordinates as number[][];
        if (!map || !coords || coords.length < 2) return null;

        const [startLng, startLat] = coords[0];
        const [endLng, endLat] = coords[coords.length - 1];

        const startElev = map.queryTerrainElevation({ lng: startLng, lat: startLat });
        const endElev = map.queryTerrainElevation({ lng: endLng, lat: endLat });
        if (startElev == null || endElev == null) return null;

        return Math.abs(startElev - endElev) * 3.28084;
    };

    return (
        <>
            <Dropdown
                className={styles.dropdown}
                title="Measure"
                isOpen={areMeasureToolsOpen}
                setIsOpen={setAreMeasureToolsOpen}
                icon={<MeasureIcon />}
            >
                <ul>
                    <li>
                        <label
                            className={!drawMapStore.isDrawControlAvailable() ? styles.inactive : ""}
                            onClick={() => {
                                if (drawMapStore.isDrawControlAvailable()) {
                                    setAreMeasureToolsOpen(false);
                                    drawMapStore.drawMeasureArea();
                                }
                            }}
                        >Area</label>
                    </li>
                    <li>
                        <label
                            className={!drawMapStore.isDrawControlAvailable() ? styles.inactive : ""}
                            onClick={() => {
                                if (drawMapStore.isDrawControlAvailable()) {
                                    setAreMeasureToolsOpen(false);
                                    drawMapStore.drawMeasureDistance();
                                }
                            }}
                        >Distance</label>
                    </li>
                </ul>
            </Dropdown>
            {drawMapStore.mode === DrawMapMode.MeasureArea && !!drawMapStore.result && (
                <div className={isHalfScreenMap ? commonStyles.halfWidthOnMapPanel : commonStyles.onMapPanel}>
                    Total Area: {formatNumber(turf.area(drawMapStore.result) * 0.0002471)} Acres / {formatNumber(turf.area(drawMapStore.result) * 10.76391)} Sq-Ft
                    <CloseIcon className={commonStyles.clickableIcon} onClick={() => drawMapStore.clear()} />
                </div>
            )}
            {drawMapStore.mode === DrawMapMode.MeasureDistance && !!drawMapStore.result && (
                <div className={isHalfScreenMap ? commonStyles.halfWidthOnMapPanel : commonStyles.onMapPanel}>
                    Total Distance: {formatNumber(turf.length(drawMapStore.result, { units: 'miles' }) * 5280)} Ft
                    {getElevationChangeFt() != null && (
                        <><br />Elevation Change: {formatNumber(getElevationChangeFt()!)} Ft</>
                    )}
                    <CloseIcon className={commonStyles.clickableIcon} onClick={() => drawMapStore.clear()} />
                </div>
            )}
        </>
    );
}

export default observer(MeasureDropdown);